import { Compass, FileCode2, Layers, ArrowRight } from 'lucide-react'
import { Link } from 'react-router-dom'
import { useAuth } from '@/lib/auth'
import { getApiBase } from '@/lib/api-base'

const FEATURES = [
  {
    icon: Layers,
    title: 'Every layer that matters',
    body:
      'Country, state, county, city, ZCTA, congressional district, federal judicial district, and metro area (CBSA). Resolved in one call.',
  },
  {
    icon: Compass,
    title: 'Boundary distance by default',
    body:
      'Miles, kilometers, bearing, and edge context for every returned geography. National borders, coastlines, and territorial boundaries called out explicitly.',
  },
  {
    icon: FileCode2,
    title: 'Source provenance, always',
    body:
      'Every layer carries source ID, vintage, and effective date. Built on public, openly published geographic data.',
  },
] as const

const EXAMPLE_CURL = `curl '${getApiBase()}/location?lat=32.2226&lon=-110.9747' \\
  -H "Authorization: Bearer $GEOBORDER_API_KEY"`

export function ApiCta() {
  const { session } = useAuth()
  const signedIn = Boolean(session)
  const primaryHref = signedIn ? '/account/keys' : '/sign-up'
  const primaryLabel = signedIn ? 'Manage API keys' : 'Create account & get API key'

  return (
    <section className="w-full bg-[var(--color-surface)] py-20 sm:py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        {/* Eyebrow + headline. No outer width clamp — the headline + paragraph
            should breathe across the same width as the three feature cards
            below, instead of stopping at 2/3 of the container. */}
        <div>
          <span className="inline-block text-[11px] font-medium uppercase tracking-[0.18em] text-[var(--color-accent)] font-[var(--font-mono)]">
            Developer API
          </span>
          <h2 className="mt-3 text-3xl sm:text-4xl font-semibold tracking-tight text-[var(--color-text-strong)] leading-tight">
            Drop in jurisdiction intelligence with one API call.
          </h2>
          <p className="mt-4 text-[15px] leading-relaxed text-[var(--color-muted)]">
            GeoBorder resolves a coordinate to its full stack of U.S. and global
            jurisdictions in a single request. No external API hops at
            request time. Every boundary lives locally in PostGIS with full
            source provenance.
          </p>
        </div>

        {/* Feature cards */}
        <div className="mt-12 grid gap-5 sm:grid-cols-3">
          {FEATURES.map(({ icon: Icon, title, body }) => (
            <div
              key={title}
              className={[
                'rounded-[var(--radius-lg)] p-6',
                'border border-[var(--color-border)]',
                'bg-[var(--color-elev)]',
              ].join(' ')}
            >
              <span
                aria-hidden
                className={[
                  'inline-flex items-center justify-center w-10 h-10 rounded-[10px]',
                  'bg-[var(--color-accent-soft)] text-[var(--color-accent)]',
                ].join(' ')}
              >
                <Icon size={20} strokeWidth={2.2} />
              </span>
              <h3 className="mt-4 text-base font-semibold tracking-tight text-[var(--color-text-strong)]">
                {title}
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-[var(--color-muted)]">
                {body}
              </p>
            </div>
          ))}
        </div>

        {/* Code preview + CTA */}
        <div className="mt-14 grid gap-8 md:grid-cols-2 md:items-center">
          <div>
            <h3 className="text-xl font-semibold tracking-tight text-[var(--color-text-strong)]">
              Designed for developers and AI agents.
            </h3>
            <p className="mt-3 text-[15px] leading-relaxed text-[var(--color-muted)]">
              Stable IDs, source metadata on every layer, and a response shape
              friendly to both human developers and AI agents.
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <Link
                to={primaryHref}
                className={[
                  'inline-flex items-center gap-1.5 px-4 py-2.5 rounded-[10px]',
                  'text-sm font-medium',
                  'bg-[var(--color-accent)] text-[var(--color-surface)]',
                  'hover:bg-[var(--color-accent-hover)] transition-colors',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--color-surface)]',
                ].join(' ')}
              >
                {primaryLabel}
                <ArrowRight size={16} aria-hidden />
              </Link>
              <Link
                to="/api"
                className={[
                  'inline-flex items-center gap-1.5 px-4 py-2.5 rounded-[10px]',
                  'text-sm font-medium',
                  'text-[var(--color-text)] border border-[var(--color-border)]',
                  'hover:border-[var(--color-accent)]/60 hover:text-[var(--color-accent)] transition-colors',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--color-surface)]',
                ].join(' ')}
              >
                Read the docs
              </Link>
            </div>
          </div>

          <div
            className={[
              'rounded-[var(--radius-lg)] overflow-hidden',
              'border border-[var(--color-border)]',
              'bg-[var(--color-elev)]',
            ].join(' ')}
          >
            <div className="flex items-center gap-2 px-4 py-2.5 border-b border-[var(--color-border)] font-[var(--font-mono)] text-[11px] text-[var(--color-muted)] uppercase tracking-[0.1em]">
              <span className="w-2.5 h-2.5 rounded-full bg-[var(--color-signal)]" />
              <span className="w-2.5 h-2.5 rounded-full bg-[var(--color-accent)]" />
              <span className="w-2.5 h-2.5 rounded-full bg-[var(--color-cream)]" />
              <span className="ml-3">terminal</span>
            </div>
            <pre className="px-4 py-4 font-[var(--font-mono)] text-[12.5px] leading-relaxed text-[var(--color-text)] whitespace-pre-wrap break-all">
              {EXAMPLE_CURL}
            </pre>
          </div>
        </div>
      </div>
    </section>
  )
}
